import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "wasp/client/auth";
import {
  generateCheckoutSession,
  changeSubscriptionPlan,
  syncStripeSubscription,
  useAction,
} from "wasp/client/operations";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { Button } from "../client/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardTitle,
} from "../client/components/ui/card";
import { CheckCircle, Sparkles, Clock } from "lucide-react";
import { cn } from "../client/utils";
import useTranslation from "../client/hooks/useTranslation";
import {
  PaymentPlanId,
  paymentPlans,
  SubscriptionStatus,
  type PlanTier,
} from "../payment/plans";
import { getPaymentPlanCards } from "../payment/PricingPage";
import { toast } from "../client/hooks/use-toast";
import { trackUpgradeStarted } from "../client/utils/posthog";
import { BeamButton } from "../client/components/ui/beam-button";

const bestDealPaymentPlanId: PaymentPlanId = PaymentPlanId.Pro;

// Refus serveur des checkouts / changements de plan pour un membre B2B actif.
const B2B_GRANT_ACTIVE_ERROR = "b2b_grant_active";

interface PlanSelectionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function PlanSelectionModal({
  open,
  onOpenChange,
}: PlanSelectionModalProps) {
  const t = useTranslation();
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('annual');
  const generateCheckoutSessionFn = useAction(generateCheckoutSession);
  const changeSubscriptionPlanFn = useAction(changeSubscriptionPlan);
  const syncStripeSubscriptionFn = useAction(syncStripeSubscription);
  const paymentPlanCards = getPaymentPlanCards(t);

  // Only offer plans strictly above the user's current tier. A Pro user should
  // see Max, not the Pro they already have; a Free user sees both.
  const currentPlan = user?.subscriptionPlan;
  const isOnProTier =
    currentPlan === PaymentPlanId.Pro || currentPlan === PaymentPlanId.ProAnnual;
  const basePlans = isOnProTier
    ? [PaymentPlanId.Max]
    : [PaymentPlanId.Pro, PaymentPlanId.Max];
  const isSinglePlan = basePlans.length === 1;

  // A user with a live subscription changes plan in place (proration / schedule);
  // only Free / expired users go through a fresh checkout.
  const hasActiveSubscription =
    !!user?.subscriptionStatus &&
    user.subscriptionStatus !== SubscriptionStatus.Deleted &&
    !!user.subscriptionPlan &&
    user.subscriptionPlan !== PaymentPlanId.Free;

  // For an existing subscriber the periodicity is fixed by their current plan
  // (a change stays annual/monthly server-side), so the toggle is hidden and the
  // cards render in the user's actual periodicity. Free users pick via the toggle.
  const currentIsAnnual =
    currentPlan === PaymentPlanId.ProAnnual ||
    currentPlan === PaymentPlanId.MaxAnnual;
  const effectiveBillingPeriod: 'monthly' | 'annual' = hasActiveSubscription
    ? (currentIsAnnual ? 'annual' : 'monthly')
    : billingPeriod;

  function resolvedPlanId(planId: PaymentPlanId): PaymentPlanId {
    if (effectiveBillingPeriod === 'annual') {
      if (planId === PaymentPlanId.Pro) return PaymentPlanId.ProAnnual;
      if (planId === PaymentPlanId.Max) return PaymentPlanId.MaxAnnual;
    }
    return planId;
  }

  async function handleSelectPlan(
    planId: PaymentPlanId,
    opts?: { forceCheckout?: boolean },
  ) {
    if (!user) {
      onOpenChange(false);
      navigate("/signup");
      return;
    }

    // Existing subscriber → upgrade in place (proration). Only Free / expired
    // users hit a fresh checkout. forceCheckout short-circuits this when the
    // change action already determined a checkout is needed (avoids a loop).
    if (hasActiveSubscription && !opts?.forceCheckout) {
      await handleChangePlan(planId === PaymentPlanId.Max ? "max" : "pro");
      return;
    }

    const resolved = resolvedPlanId(planId);
    try {
      setIsPaymentLoading(true);

      const checkoutResults = await generateCheckoutSessionFn(resolved);

      if (resolved === PaymentPlanId.Pro) {
        trackUpgradeStarted("pro");
      } else if (resolved === PaymentPlanId.ProAnnual) {
        trackUpgradeStarted("pro_annual");
      } else if (resolved === PaymentPlanId.Max) {
        trackUpgradeStarted("max");
      } else if (resolved === PaymentPlanId.MaxAnnual) {
        trackUpgradeStarted("max_annual");
      }

      if (checkoutResults?.sessionUrl) {
        window.location.href = checkoutResults.sessionUrl;
      } else {
        throw new Error("Error generating checkout session URL");
      }
    } catch (error: unknown) {
      setIsPaymentLoading(false);
      const rawMessage =
        error instanceof Error
          ? error.message
          : "Error processing payment. Please try again later.";
      const errorMessage = rawMessage.includes(B2B_GRANT_ACTIVE_ERROR)
        ? t.b2bMember.checkoutBlocked
        : rawMessage;
      toast({
        title: t.common.error,
        description: errorMessage,
        variant: "destructive",
      });
    }
  }

  async function handleChangePlan(tier: PlanTier) {
    try {
      setIsPaymentLoading(true);
      const result = await changeSubscriptionPlanFn({ tier });

      switch (result.kind) {
        case "upgraded":
          trackUpgradeStarted(result.plan);
          // Pull the new plan into our DB + refresh the client immediately
          // (this action writes User, so Wasp re-fetches useAuth) instead of
          // waiting for the async webhook.
          await syncStripeSubscriptionFn(undefined);
          toast({
            title: t.dashboard.planChange.upgradedTitle,
            description: t.dashboard.planChange.upgradedDescription,
          });
          onOpenChange(false);
          break;
        case "requires_action":
          // The proration charge needs 3DS/SCA. Send the user to Stripe's hosted
          // invoice page to authenticate (consistent with our hosted checkout).
          // The invoice.paid webhook finalizes the plan once they complete it.
          window.location.href = result.hostedInvoiceUrl;
          return;
        case "needs_checkout":
          // No live subscription after all — fall back to a fresh checkout.
          // forceCheckout avoids re-entering the change path (the client `user`
          // may still look "subscribed" while Stripe disagrees).
          await handleSelectPlan(
            tier === "max" ? PaymentPlanId.Max : PaymentPlanId.Pro,
            { forceCheckout: true },
          );
          return;
        case "noop":
          toast({ title: t.dashboard.planChange.alreadyOnPlan });
          onOpenChange(false);
          break;
      }
    } catch (error: unknown) {
      const rawMessage =
        error instanceof Error
          ? error.message
          : "Error processing plan change. Please try again later.";
      const errorMessage = rawMessage.includes(B2B_GRANT_ACTIVE_ERROR)
        ? t.b2bMember.checkoutBlocked
        : rawMessage;
      toast({
        title: t.common.error,
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsPaymentLoading(false);
    }
  }

  // Membre B2B : Max offert par un partenaire, aucun plan à proposer.
  if (user?.b2bGrantId) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              {t.dashboard.selectPlan}
            </DialogTitle>
            <DialogDescription className="text-base">
              {t.b2bMember.checkoutBlocked}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t.common.close}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">
            {t.dashboard.selectPlan}
          </DialogTitle>
          <DialogDescription className="text-base">
            {t.dashboard.selectPlanDescription}
          </DialogDescription>
        </DialogHeader>

        {/* Billing toggle — only for new subscribers; existing subscribers keep
            their current periodicity, so we don't offer a choice that's ignored. */}
        {!hasActiveSubscription && (
        <div className="flex justify-center mt-4">
          <div className="inline-flex items-center rounded-full border border-border bg-muted p-1 gap-1">
            <button
              type="button"
              onClick={() => setBillingPeriod('monthly')}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200",
                billingPeriod === 'monthly'
                  ? "bg-background text-foreground shadow"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t.pricing.monthly}
            </button>
            <button
              type="button"
              onClick={() => setBillingPeriod('annual')}
              className={cn(
                "rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200",
                billingPeriod === 'annual'
                  ? "bg-background text-foreground shadow"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {t.pricing.annual}
            </button>
          </div>
        </div>
        )}

        <div
          className={cn(
            "grid grid-cols-1 gap-4 mt-6",
            isSinglePlan ? "sm:max-w-md sm:mx-auto" : "sm:grid-cols-2",
          )}
        >
          {basePlans.map((planId) => {
            // Highlight the recommended plan; when only one plan is offered
            // (upgrade from Pro → Max) that single card is the highlighted one.
            const isBestDeal = isSinglePlan || planId === bestDealPaymentPlanId;
            const displayPlanId = resolvedPlanId(planId);
            const card = paymentPlanCards[displayPlanId];
            return (
              <Card
                key={planId}
                className={cn(
                  "group relative flex flex-col overflow-hidden transition-all duration-300",
                  "hover:shadow-xl hover:shadow-primary/10",
                  isBestDeal
                    ? "border-primary/50 bg-gradient-to-br from-primary/5 via-background to-primary/5 ring-2 ring-primary shadow-lg"
                    : "border-border hover:border-primary/30"
                )}
              >
                {isBestDeal && paymentPlanCards[planId].badge && (
                  <div className="absolute right-0 top-0 z-10">
                    <div className="bg-gradient-to-r from-primary to-primary/80 px-4 py-1.5 text-xs font-bold text-primary-foreground shadow-lg">
                      <div className="flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        {paymentPlanCards[planId].badge}
                      </div>
                    </div>
                  </div>
                )}
                <CardContent className="relative z-10 flex h-full flex-col justify-between p-6">
                  <div>
                    <CardTitle className="text-foreground text-xl font-bold leading-7">
                      {paymentPlanCards[planId].name}
                    </CardTitle>
                    <p className="text-muted-foreground mt-2 text-sm leading-5">
                      {card.description}
                    </p>
                    <div className="mt-6 flex items-baseline gap-x-1">
                      <span
                        className={cn(
                          "text-foreground font-bold tracking-tight",
                          isBestDeal ? "text-4xl" : "text-3xl"
                        )}
                      >
                        {card.price}
                      </span>
                      <span className="text-muted-foreground text-sm font-semibold leading-6">
                        {t.pricing.perMonth}
                      </span>
                    </div>
                    {effectiveBillingPeriod === 'annual' && (card.savingsPercent || card.savings) && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {card.savingsPercent && (
                          <span className="inline-flex items-center rounded-full bg-primary/15 px-2.5 py-0.5 text-xs font-semibold text-primary">
                            {card.savingsPercent}
                          </span>
                        )}
                        {card.savings && (
                          <span className="text-xs text-muted-foreground">
                            {card.savings}
                          </span>
                        )}
                      </div>
                    )}
                    <ul
                      role="list"
                      className="text-muted-foreground mt-6 space-y-2.5 text-sm leading-5"
                    >
                      {card.features.map((feature) => {
                        const label = typeof feature === "string" ? feature : feature.label;
                        const badge = typeof feature === "string" ? undefined : feature.badge;
                        return (
                          <li key={label} className="flex items-start gap-x-2.5">
                            {badge === "soon" ? (
                              <Clock className="mt-0.5 h-4 w-4 flex-none shrink-0 text-muted-foreground/60" aria-hidden="true" />
                            ) : (
                              <CheckCircle
                                className={cn(
                                  "mt-0.5 h-4 w-4 flex-none shrink-0",
                                  isBestDeal ? "text-primary" : "text-primary/70"
                                )}
                                aria-hidden="true"
                              />
                            )}
                            <span
                              className={cn(
                                "flex-1 flex flex-wrap items-center gap-1.5",
                                badge === "soon" && "text-muted-foreground/60",
                              )}
                            >
                              {label}
                              {badge === "soon" && (
                                <span className="inline-flex items-center rounded-full border border-primary/30 bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-primary">
                                  {t.common.soon}
                                </span>
                              )}
                              {badge === "beta" && (
                                <span className="inline-flex items-center rounded-full border border-warning/40 bg-warning/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase leading-none tracking-wide text-warning">
                                  {t.common.beta}
                                </span>
                              )}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </CardContent>
                <CardFooter className="relative z-10 p-6 pt-0">
                  {isBestDeal ? (
                    <BeamButton
                      fullWidth
                      onClick={() => handleSelectPlan(planId)}
                      disabled={isPaymentLoading}
                    >
                      {t.landing.hero.ctaGetStarted}
                    </BeamButton>
                  ) : (
                    <Button
                      onClick={() => handleSelectPlan(planId)}
                      variant="outline"
                      className="w-full transition-all duration-300 !bg-transparent hover:border-primary hover:!bg-transparent hover:!text-foreground"
                      size="lg"
                      disabled={isPaymentLoading}
                    >
                      {t.landing.hero.ctaGetStarted}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isPaymentLoading}
          >
            {t.dashboard.continueWithFree || t.common.cancel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

